/**
 * Service for assigning players to positions in a formation (depth chart).
 * Used by Coach Steve and other flows that need to update depth chart from natural language or API.
 */

import { supabase } from '../lib/supabase';
import type { Sport } from '../types/core';
import {
  FOOTBALL_OFFENSIVE_FORMATIONS,
  FOOTBALL_DEFENSIVE_FORMATIONS,
  FOOTBALL_SPECIAL_TEAMS_FORMATIONS,
  FLAG_FOOTBALL_OFFENSIVE_FORMATIONS,
} from '../data/sportsFormations';
import { SPORT_RULESETS } from '../data/sportRulesets';

export interface FormationResolution {
  unit: string;
  formationName: string;
  positionFullName: string;
}

const UNITS = ['Offense', 'Defense', 'Special Teams'] as const;

/**
 * Resolve formation name + position to unit and position full name.
 * Tries predefined formations first (Football/Flag Football), then custom formations for the team.
 */
export async function resolveFormationAndPosition(
  sport: Sport,
  formationName: string,
  positionKey: string,
  teamId: string
): Promise<FormationResolution | null> {
  const normalizedFormation = formationName.trim();
  const normalizedPosition = positionKey.toUpperCase().trim();
  if (!normalizedFormation || !normalizedPosition) return null;

  const ruleset = SPORT_RULESETS[sport];
  if (!ruleset?.formations.enabled) return null;

  // Predefined formations (Football and Flag Football)
  if (sport === 'Football') {
    for (const unit of UNITS) {
      const list =
        unit === 'Offense'
          ? FOOTBALL_OFFENSIVE_FORMATIONS
          : unit === 'Defense'
            ? FOOTBALL_DEFENSIVE_FORMATIONS
            : FOOTBALL_SPECIAL_TEAMS_FORMATIONS;
      const formation = list.find((f) => f.name.toLowerCase() === normalizedFormation.toLowerCase());
      if (formation) {
        const pos = formation.positions.find((p) => p.key.toUpperCase() === normalizedPosition);
        if (pos) return { unit, formationName: formation.name, positionFullName: pos.fullName || pos.key };
        return null; // formation found but position not in it
      }
    }
  }

  if (sport === 'FlagFootball') {
    const list = FLAG_FOOTBALL_OFFENSIVE_FORMATIONS;
    const formation = list.find((f) => f.name.toLowerCase() === normalizedFormation.toLowerCase());
    if (formation) {
      const pos = formation.positions.find((p) => p.key.toUpperCase() === normalizedPosition);
      if (pos) return { unit: 'Offense', formationName: formation.name, positionFullName: pos.fullName || pos.key };
      return null;
    }
    // Flag defense: inline formations in useFormations (Man Coverage, Zone Coverage)
    if (normalizedFormation.toLowerCase() === 'man coverage' || normalizedFormation.toLowerCase() === 'zone coverage') {
      const positions =
        normalizedFormation.toLowerCase() === 'man coverage'
          ? ['CB1', 'CB2', 'S', 'LB1', 'LB2']
          : ['CB1', 'CB2', 'S', 'LB1', 'LB2'];
      if (positions.map((p) => p.toUpperCase()).includes(normalizedPosition))
        return { unit: 'Defense', formationName: normalizedFormation, positionFullName: normalizedPosition };
      return null;
    }
  }

  // Custom formations: query by team and name (any unit)
  const { data: customRows } = await supabase
    .from('custom_formations')
    .select('unit, name, positions')
    .eq('team_id', teamId)
    .eq('sport', sport)
    .ilike('name', normalizedFormation);

  const custom = customRows?.[0];
  if (custom?.positions && Array.isArray(custom.positions)) {
    const positions = custom.positions as Array<{ key?: string; label?: string; name?: string; fullName?: string; full_name?: string }>;
    const pos = positions.find(
      (p) => (p.key || p.label || p.name || '').toUpperCase() === normalizedPosition
    );
    if (pos)
      return {
        unit: custom.unit,
        formationName: custom.name,
        positionFullName: pos.fullName || pos.full_name || pos.label || pos.name || pos.key || normalizedPosition,
      };
    return null;
  }

  return null;
}

export interface AssignPlayerToFormationResult {
  success: boolean;
  error?: string;
  /** If the player was already in another position in this formation, we moved them; this is that previous position. */
  previousPosition?: string;
}

/**
 * Assign a player to a position in a formation (re-assign = one position per player per formation).
 * Removes this player from any other position in the same formation first, then assigns the new slot.
 * depth defaults to 1 (first string).
 */
export async function assignPlayerToFormation(
  teamId: string,
  sport: Sport,
  formationName: string,
  positionKey: string,
  playerId: string,
  depth: number = 1
): Promise<AssignPlayerToFormationResult> {
  const resolved = await resolveFormationAndPosition(sport, formationName, positionKey, teamId);
  if (!resolved) {
    return { success: false, error: 'Formation or position not found for this sport and team.' };
  }

  const positionNorm = positionKey.toUpperCase().trim();

  try {
    // 1) Find any existing assignment of this player in this formation (so we only have one position per player per formation)
    const { data: existingPlayerEntries } = await supabase
      .from('depth_chart')
      .select('id, position')
      .eq('team_id', teamId)
      .eq('unit', resolved.unit)
      .eq('formation', resolved.formationName)
      .eq('player_id', playerId);

    let previousPosition: string | undefined;
    if (existingPlayerEntries && existingPlayerEntries.length > 0) {
      const otherPosition = existingPlayerEntries.find((e) => e.position !== positionNorm);
      if (otherPosition) previousPosition = otherPosition.position;
      // Remove this player from all positions in this formation (we'll add to the new one below)
      const { error: removeError } = await supabase
        .from('depth_chart')
        .delete()
        .eq('team_id', teamId)
        .eq('unit', resolved.unit)
        .eq('formation', resolved.formationName)
        .eq('player_id', playerId);
      if (removeError) {
        console.error('[depthChartAssignmentService] remove player from other positions', removeError);
        return { success: false, error: removeError.message };
      }
    }

    // 2) Free the slot we're filling (in case another player was there)
    const { error: deleteSlotError } = await supabase
      .from('depth_chart')
      .delete()
      .eq('team_id', teamId)
      .eq('unit', resolved.unit)
      .eq('formation', resolved.formationName)
      .eq('position', positionNorm)
      .eq('depth', depth);
    if (deleteSlotError) {
      console.error('[depthChartAssignmentService] delete slot error', deleteSlotError);
      return { success: false, error: deleteSlotError.message };
    }

    // 3) Insert new entry
    const insertData: Record<string, unknown> = {
      team_id: teamId,
      unit: resolved.unit,
      formation: resolved.formationName,
      position: positionNorm,
      position_full_name: resolved.positionFullName,
      depth,
      player_id: playerId,
      color: '',
      is_template: false,
    };
    const { error: insertError } = await supabase.from('depth_chart').insert(insertData);
    if (insertError) {
      console.error('[depthChartAssignmentService] insert error', insertError);
      return { success: false, error: insertError.message };
    }

    return { success: true, previousPosition };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
}
